import type { QueryClient } from "@tanstack/react-query";
import {
  eligibleMakeupAttendanceQueryKey,
  getMakeupSessionsQueryKey,
  MEMBER_MAKEUP_SESSIONS_STALE_TIME_MS,
} from "@/features/makeups/hooks/makeup-sessions-query-key";
import { makeupAnnualPeriodDateRange } from "@/features/makeups/shared/makeupAnnualLimit";
import { DEFAULT_MAKEUPS_ANNUAL_PERIOD } from "@/features/makeups/shared/makeupAnnualPeriod";
import { makeupEligibilityDateRange } from "@/features/makeups/shared/makeupEligibility";
import { DEFAULT_MAKEUPS_EXPIRY_IN_DAYS } from "@/features/makeups/shared/makeupExpirySettings";
import { fetchAttendanceLogsForStudents } from "@/lib/services/attendanceService";
import {
  MEMBER_MAKEUP_SESSIONS_PAGE_SIZE,
  searchMakeupSessions,
  toMakeupMemberId,
} from "@/lib/services/makeupService";
import { QueryKeys } from "@/lib/types";
import type { MakeupsEligibilitySettingsResult } from "@/lib/utils/makeup-settings/makeup-settings-api";
import { prefetchClassesByIds } from "./prefetchClassesByIds";
import { prefetchClassesEnrollmentsByStudentIds } from "./prefetchClassesEnrollmentsByStudentIds";
import { prefetchMemberDashboardMemberSettings } from "./prefetchMemberDashboardMemberSettings";

const ELIGIBLE_ATTENDANCE_STALE_TIME_MS = 60_000;

export type PrefetchMemberMakeupsBundleOptions = {
  /** Caller already ran `prefetchMemberDashboardMemberSettings` on this client. */
  skipMemberSettings?: boolean;
  /** Caller already ran `prefetchClassesEnrollmentsByStudentIds` on this client. */
  skipEnrollments?: boolean;
  /**
   * Caller already warmed class-by-id queries for the makeup class set.
   * Prefer leaving this false on home: home often prefetches only active enrollments,
   * while makeups may need class details for dropped enrollments too.
   */
  skipClasses?: boolean;
};

function studentIdsKey(studentIds: number[]): string {
  return [...new Set(studentIds.filter((id) => Number.isFinite(id) && id > 0))]
    .sort((a, b) => a - b)
    .join(",");
}

async function prefetchMemberMakeupSessionsSearch(
  queryClient: QueryClient,
  accessToken: string,
  filters: Parameters<typeof searchMakeupSessions>[1]["filters"] = {},
) {
  await queryClient.prefetchQuery({
    queryKey: getMakeupSessionsQueryKey({
      page: 1,
      pageSize: MEMBER_MAKEUP_SESSIONS_PAGE_SIZE,
      lastSelectedId: undefined,
      active: undefined,
      filters,
    }),
    queryFn: () =>
      searchMakeupSessions(accessToken, {
        page: 1,
        pageSize: MEMBER_MAKEUP_SESSIONS_PAGE_SIZE,
        filters,
      }),
    staleTime: MEMBER_MAKEUP_SESSIONS_STALE_TIME_MS,
  });
}

/** SSR bundle for member makeups page + home makeup widget. */
export async function prefetchMemberMakeupsBundle(
  queryClient: QueryClient,
  accessToken: string,
  tenantId: number,
  studentIds: number[],
  memberId: number,
  options: PrefetchMemberMakeupsBundleOptions = {},
) {
  const {
    skipMemberSettings = false,
    skipEnrollments = false,
    skipClasses = false,
  } = options;
  const scopedMemberId = toMakeupMemberId(memberId);

  if (!skipMemberSettings) {
    await prefetchMemberDashboardMemberSettings(
      tenantId,
      queryClient,
      accessToken,
    );
  }

  const ids = studentIds.filter((id) => Number.isFinite(id) && id > 0);
  if (ids.length === 0 || scopedMemberId == null) return queryClient;

  const settings = queryClient.getQueryData<MakeupsEligibilitySettingsResult>([
    QueryKeys.MakeupEligibilitySettings,
    tenantId,
  ]);
  const expiryDays = settings?.expiryDays ?? DEFAULT_MAKEUPS_EXPIRY_IN_DAYS;
  const annualPeriod = settings?.annualPeriod ?? DEFAULT_MAKEUPS_ANNUAL_PERIOD;
  const annualRange = makeupAnnualPeriodDateRange(annualPeriod, new Date());
  const eligibilityRange = makeupEligibilityDateRange(expiryDays);
  const idsKey = studentIdsKey(ids);

  await Promise.all([
    prefetchMemberMakeupSessionsSearch(queryClient, accessToken, {
      memberId: scopedMemberId,
    }),
    prefetchMemberMakeupSessionsSearch(queryClient, accessToken, {
      memberId: scopedMemberId,
      fromDate: annualRange.fromDate,
      toDate: annualRange.toDate,
    }),
    skipEnrollments
      ? Promise.resolve()
      : prefetchClassesEnrollmentsByStudentIds(queryClient, ids, accessToken),
    queryClient.prefetchQuery({
      queryKey: eligibleMakeupAttendanceQueryKey(idsKey, expiryDays),
      queryFn: () =>
        fetchAttendanceLogsForStudents(
          accessToken,
          ids,
          eligibilityRange.fromDate,
          eligibilityRange.toDate,
        ),
      staleTime: ELIGIBLE_ATTENDANCE_STALE_TIME_MS,
    }),
  ]);

  if (skipClasses) return queryClient;

  const sortedIds = [...ids].sort((a, b) => a - b).join(",");
  const enrollmentsResponse = queryClient.getQueryData<{
    items?: Array<{ _class?: { classId?: number | string | null } | null }>;
  }>([QueryKeys.ClassesEnrollmentsByStudentId, "multiple", sortedIds]);

  const classIds = Array.from(
    new Set(
      (enrollmentsResponse?.items ?? [])
        .map((item) => Number(item._class?.classId))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  );

  if (classIds.length > 0) {
    await prefetchClassesByIds(queryClient, classIds, accessToken);
  }

  return queryClient;
}
